import { useCallback, useEffect, useMemo, useState } from 'react';

import { useWindowSize } from 'shared-components/utils/hooks/use-window-size';

// Switches between hover & click event handlers based on window size and passed breakpoint
// returns boolean flag that indicates if element should animate
export const useResponsiveAnimation = (ref, breakpoint = 576) => {
  const [screen, setScreen] = useState('desktop');
  const [animate, setAnimate] = useState(false);

  const { width } = useWindowSize();

  useEffect(() => {
    setScreen(breakpoint <= width ? 'desktop' : 'mobile');
  }, [width, breakpoint]);

  const showAnimation = useCallback(() => {
    setAnimate(true);
  }, [animate]);

  const hideAnimation = useCallback(() => {
    setAnimate(false);
  }, [animate]);

  const toggleAnimation = useCallback(() => {
    setAnimate(prev => !prev);
  }, [animate]);

  const subscribeHover = useCallback(() => {
    ref.current.addEventListener('mouseover', showAnimation);
    ref.current.addEventListener('mouseleave', hideAnimation);
  }, [showAnimation, hideAnimation, ref]);

  const unsubscribeHover = useCallback(() => {
    ref.current.removeEventListener('mouseover', showAnimation);
    ref.current.removeEventListener('mouseleave', hideAnimation);
  }, [showAnimation, hideAnimation, ref]);

  const subscribeClick = useCallback(() => {
    ref.current.addEventListener('click', toggleAnimation);
  }, [toggleAnimation, ref]);

  const unsubscribeClick = useCallback(() => {
    ref.current.removeEventListener('click', toggleAnimation);
  }, [toggleAnimation, hideAnimation, ref]);

  useEffect(() => {
    if (!ref || !ref.current) {
      return;
    }

    if ('desktop' === screen) {
      unsubscribeClick();
      subscribeHover();
      return () => {
        unsubscribeHover();
      };
    }
    if ('mobile' === screen) {
      unsubscribeHover();
      subscribeClick();
      return () => {
        unsubscribeClick();
      };
    }
  }, [ref, screen, subscribeHover, unsubscribeHover]);

  return animate;
};

// Returns Desktop/Mobile value based on window size and passed breakpoint
export const useResponsiveValue = (Desktop, Mobile, breakpoint = 576) => {
  const [isMobile, setIsMobile] = useState(false);

  const { width } = useWindowSize();

  useEffect(() => {
    setIsMobile(breakpoint > width);
  }, [width, breakpoint]);

  return useMemo(() => isMobile ? Mobile : Desktop, [isMobile]);
};

// Dynamically converts seconds into hh:mm:ss format
// e.g. 5.03 => 0:05
export const formatVideoTime = (secondsToFormat) => {
  const secondsNumber = parseInt(secondsToFormat, 10);
  let hours = Math.floor(secondsNumber / 3600);
  let minutes = Math.floor((secondsNumber - (hours * 3600)) / 60);
  let seconds = secondsNumber - (hours * 3600) - (minutes * 60);
  if (0 !== +minutes) {
    minutes = '0' + minutes;
  }
  if (10 > seconds) {
    seconds = '0' + seconds;
  }
  if (0 === +hours) {
    return `${minutes}:${seconds}`;
  }
  return `${hours}:${minutes}:${seconds}`;
};

export const useVideoPlayer = (ref) => {
  const [currentSecond, setCurrentSecond] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [playing, setPlaying] = useState(false);
  const playHandler = () => {
    setPlaying(true);
  };
  const pauseHandler = () => {
    setPlaying(false);
  };
  const pause = () => {
    if (ref && ref.current && !ref.current.paused) {
      ref.current.pause();
    }
  };
  const play = () => {
    if (ref && ref.current && ref.current.paused) {
      ref.current.play();
    }
  };
  const handleTimeUpdate = () => {
    setCurrentSecond(ref.current?.currentTime || 0);
  };
  const handleLoad = () => {
    setTotalSeconds(ref.current?.duration);
  };

  return {
    currentSecond,
    totalSeconds,
    playing,
    playHandler,
    pauseHandler,
    pause,
    play,
    handleTimeUpdate,
    handleLoad
  };
};
